/**
 * useTransferState Hook
 * 
 * Manages transfer dialog state and handlers.
 */

import { useState, useEffect, useRef, useMemo, ChangeEvent } from "react";
import { Account, Currency } from "../../../types";
import { calculateTransferRate, formatRate, getConvertedAmount } from "../../../utils/accountHelpers";

export interface TransferData {
  fromAccountId: string;
  toAccountId: string;
  amount: string;
  transferRate: string;
  date: string;
  time?: string;
  note: string;
  attachmentFile: File | null;
  attachmentUrl: string | null;
  attachmentName: string | null;
  attachmentType: string | null;
  feeAmount?: string;
  feeAccountId?: string;
}

interface UseTransferStateParams {
  accounts: Account[];
  currencies: Currency[];
  defaultCurrency: string;
}

interface UseTransferStateReturn {
  transferDialogOpen: boolean;
  setTransferDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  transferData: TransferData;
  setTransferData: React.Dispatch<React.SetStateAction<TransferData>>;
  uploadingAttachment: boolean;
  setUploadingAttachment: React.Dispatch<React.SetStateAction<boolean>>;
  transferFileInputRef: React.RefObject<HTMLInputElement | null>;
  handleTransferClick: () => void;
  handleTransferDialogClose: () => void;
  handleTransferFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleRemoveTransferAttachment: () => void;
  getConvertedAmountLocal: () => number | null;
  isTransferFormValid: boolean;
}

/**
 * Hook to manage transfer dialog state
 */
export function useTransferState({
  accounts,
  currencies,
  defaultCurrency,
}: UseTransferStateParams): UseTransferStateReturn {
  const [transferDialogOpen, setTransferDialogOpen] = useState<boolean>(false);
  const [transferData, setTransferData] = useState<TransferData>({
    fromAccountId: "",
    toAccountId: "",
    amount: "",
    transferRate: "",
    date: new Date().toISOString().split("T")[0], // Default to today
    note: "",
    attachmentFile: null,
    attachmentUrl: null,
    attachmentName: null,
    attachmentType: null,
  });
  const [uploadingAttachment, setUploadingAttachment] = useState<boolean>(false);
  const transferFileInputRef = useRef<HTMLInputElement>(null);

  // Update transfer rate when accounts change
  useEffect(() => {
    if (transferData.fromAccountId && transferData.toAccountId) {
      const fromAccount = accounts.find((acc) => acc.id === transferData.fromAccountId);
      const toAccount = accounts.find((acc) => acc.id === transferData.toAccountId);
      
      if (fromAccount && toAccount && fromAccount.currency !== toAccount.currency) {
        const calculatedRate = calculateTransferRate(fromAccount.currency, toAccount.currency, defaultCurrency, currencies);
        // Only auto-populate if rate field is empty or user hasn't manually edited it
        if (!transferData.transferRate || transferData.transferRate === "") {
          setTransferData((prev) => ({
            ...prev,
            transferRate: formatRate(calculatedRate),
          }));
        }
      } else if (fromAccount && toAccount && fromAccount.currency === toAccount.currency) {
        // Same currency, clear rate field
        setTransferData((prev) => ({
          ...prev,
          transferRate: "",
        }));
      }
    }
  }, [transferData.fromAccountId, transferData.toAccountId, accounts, currencies, defaultCurrency]);

  // Handle transfer click
  const handleTransferClick = (): void => {
    setTransferData({
      fromAccountId: "",
      toAccountId: "",
      amount: "",
      transferRate: "",
      date: new Date().toISOString().split("T")[0], // Default to today
      note: "",
      attachmentFile: null,
      attachmentUrl: null,
      attachmentName: null,
      attachmentType: null,
      feeAmount: undefined,
      feeAccountId: undefined,
    });
    setTransferDialogOpen(true);
  };

  // Handle transfer dialog close
  const handleTransferDialogClose = (): void => {
    setTransferDialogOpen(false);
    setTransferData({
      fromAccountId: "",
      toAccountId: "",
      amount: "",
      transferRate: "",
      date: new Date().toISOString().split("T")[0], // Reset to today
      note: "",
      attachmentFile: null,
      attachmentUrl: null,
      attachmentName: null,
      attachmentType: null,
    });
    if (transferFileInputRef.current) {
      transferFileInputRef.current.value = "";
    }
  };

  // Handle attachment file change
  const handleTransferFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["application/pdf", "image/png", "image/jpeg", "image/jpg"];
    const normalizedType = file.type === "image/jpg" ? "image/jpeg" : file.type;
    
    if (!allowedTypes.includes(normalizedType)) {
      alert("Please select a PDF, PNG, or JPG file");
      if (transferFileInputRef.current) {
        transferFileInputRef.current.value = "";
      }
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert("File size must be less than 10MB");
      if (transferFileInputRef.current) {
        transferFileInputRef.current.value = "";
      }
      return;
    }

    setTransferData((prev) => ({
      ...prev,
      attachmentFile: file,
      attachmentName: file.name,
      attachmentType: normalizedType,
    }));
  };

  // Handle attachment removal
  const handleRemoveTransferAttachment = (): void => {
    setTransferData((prev) => ({
      ...prev,
      attachmentFile: null,
      attachmentUrl: null,
      attachmentName: null,
      attachmentType: null,
    }));
    if (transferFileInputRef.current) {
      transferFileInputRef.current.value = "";
    }
  };

  // Calculate converted amount for live display
  const getConvertedAmountLocal = (): number | null => {
    if (!transferData.fromAccountId || !transferData.toAccountId || !transferData.amount) {
      return null;
    }

    const fromAccount = accounts.find((acc) => acc.id === transferData.fromAccountId && !acc.isArchived);
    const toAccount = accounts.find((acc) => acc.id === transferData.toAccountId && !acc.isArchived);

    if (!fromAccount || !toAccount) return null;

    const currenciesDiffer = fromAccount.currency !== toAccount.currency;
    if (!currenciesDiffer) return null;

    return getConvertedAmount(transferData.amount, transferData.transferRate);
  };

  // Validate transfer form
  const isTransferFormValid = useMemo<boolean>(() => {
    // Required fields: date, fromAccountId, toAccountId, amount
    const hasDate = Boolean(transferData.date && transferData.date.trim() !== "");
    const hasFromAccount = Boolean(transferData.fromAccountId && transferData.fromAccountId.trim() !== "");
    const hasToAccount = Boolean(transferData.toAccountId && transferData.toAccountId.trim() !== "");
    const hasAmount = Boolean(transferData.amount && transferData.amount.trim() !== "" && parseFloat(transferData.amount) > 0);
    
    // Check if accounts are different
    const accountsDifferent = Boolean(hasFromAccount && hasToAccount && transferData.fromAccountId !== transferData.toAccountId);
    
    // Check if exchange rate is required (when currencies differ)
    const fromAccount = accounts.find((acc) => acc.id === transferData.fromAccountId && !acc.isArchived);
    const toAccount = accounts.find((acc) => acc.id === transferData.toAccountId && !acc.isArchived);
    const currenciesDiffer = Boolean(fromAccount && toAccount && fromAccount.currency !== toAccount.currency);
    
    // If currencies differ, transferRate is required
    const hasTransferRate = !currenciesDiffer || Boolean(transferData.transferRate && transferData.transferRate.trim() !== "" && parseFloat(transferData.transferRate) > 0);
    
    return hasDate && hasFromAccount && hasToAccount && hasAmount && accountsDifferent && hasTransferRate;
  }, [transferData.date, transferData.fromAccountId, transferData.toAccountId, transferData.amount, transferData.transferRate, accounts]);

  return {
    transferDialogOpen,
    setTransferDialogOpen,
    transferData,
    setTransferData,
    uploadingAttachment,
    setUploadingAttachment,
    transferFileInputRef,
    handleTransferClick,
    handleTransferDialogClose,
    handleTransferFileChange,
    handleRemoveTransferAttachment,
    getConvertedAmountLocal,
    isTransferFormValid,
  };
}
